import { useState } from 'react';
import { Topbar, Sidebar } from "../../components";
import { authStaff } from '../../utils/constants';
import useConfigOptions from '../../hooks/useConfigOptions';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../slices/staffSlice';
import { fetchRequests } from '../../Requests/getRequests';
import NewClient from './NewClient';
import { show } from '../../slices/toastSlice';
import { HugeiconsIcon } from '@hugeicons/react';
import {
    SmartPhone01Icon, ArrowRight01Icon, UserIcon, FileAddIcon,
    Search01Icon, CheckmarkCircle01Icon, Clock01Icon,
} from '@hugeicons/core-free-icons';

function Index() {
    const { loanProducts } = useConfigOptions();

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loanType, setLoanType] = useState(null);
    const [phoneNum, setPhoneNum] = useState('');
    const [client, setClient] = useState(null);
    const [summary, setSummary] = useState(null);
    const [isToggled, setIsToggled] = useState(false);
    const [process, setProcess] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const handleSubmit = async () => {
        setProcess(true);
        const res = await fetchRequests(`/loans/staff/preloan`, "get", authStaff(accessToken), { phoneNum }, {});
        setProcess(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }

        if (res?.err === "Client does not exist") {
            setIsOpen(true);
            return;
        }

        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        if (res.isVerified === false) {
            dispatch(show({ type: "error", state: true, message: "Client is not verified. Please verify the client before proceeding." }));
            return;
        }

        localStorage.setItem("client", JSON.stringify({
            phone: res?.phone,
            name: res?.otherNames + " " + res?.surname
        }));
        setClient(res);
        setIsToggled(true);

        // Fire-and-forget loan history / standing lookup for the rail.
        // Failure is non-blocking — the rail falls back to identity-only.
        setSummary(null);
        const summaryRes = await fetchRequests(`/loans/staff/client-summary`, "get", authStaff(accessToken), { phoneNum }, {});
        if (summaryRes && !summaryRes.err && !summaryRes.code) setSummary(summaryRes);
    };

    const handleNav = () => {
        if (!loanType) {
            dispatch(show({ type: "error", state: true, message: "Please select a loan type" }));
            return;
        }
        navigate(`/apply/${loanType}`);
    };

    const clientReady = isToggled && client && Object.keys(client).length > 0;
    const selectedProduct = loanType && loanProducts?.find(p => (p.value ?? p.slug) === loanType);
    const initials = clientReady
        ? `${(client?.otherNames?.[0] ?? '')}${(client?.surname?.[0] ?? '')}`.toUpperCase()
        : '';
    const kycDate = client?.dateOnboarded || client?.updatedAt || client?.createdAt;
    const kycLabel = kycDate ? new Date(kycDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : null;

    return (
        <div className='main-container'>
            <Topbar />
            <Sidebar />

            <main className='main-content'>
                <section className='section-container space-y-6'>

                    {/* Page header */}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 flex items-center justify-center shrink-0">
                            <HugeiconsIcon icon={FileAddIcon} size={18} className="text-[#1e3a6e] dark:text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-50">New loan application</h1>
                            <p className="text-[15px] text-gray-400">Client stays on the left, pick a product on the right</p>
                        </div>
                    </div>

                    {/* Split workspace */}
                    <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">

                        {/* ─────────── LEFT RAIL: CLIENT ─────────── */}
                        <aside className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-4 h-fit">
                            <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase">Client</p>

                            {/* Search */}
                            <div className="space-y-2">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                        <HugeiconsIcon icon={SmartPhone01Icon} size={14} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="phone_number"
                                        name="phone_number"
                                        value={phoneNum}
                                        onChange={(e) => setPhoneNum(e.target.value)}
                                        placeholder="0XXXXXXXXX"
                                        maxLength={10}
                                        className="block w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-300 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1e3a6e]/20 focus:border-[#1e3a6e] dark:focus:border-blue-500 transition-colors"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={phoneNum.length !== 10 || process}
                                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <HugeiconsIcon icon={Search01Icon} size={13} />
                                    {process ? 'Looking up…' : 'Look up client'}
                                </button>
                            </div>

                            {/* Client card */}
                            {clientReady ? (
                                <>
                                    <div className="flex flex-col items-center text-center pt-2">
                                        <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 flex items-center justify-center text-sm font-semibold text-[#1e3a6e] dark:text-blue-300">
                                            {initials || <HugeiconsIcon icon={UserIcon} size={18} className="text-[#1e3a6e]" />}
                                        </div>
                                        <p className="mt-2 text-[15px] font-semibold text-gray-900 dark:text-gray-50 leading-tight">
                                            {client?.otherNames} {client?.surname}
                                        </p>
                                        <p className="text-sm text-gray-400">{client?.phone}</p>
                                    </div>

                                    {/* Status */}
                                    <div className="pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1.5">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-400">Verified</span>
                                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={12} />
                                                Yes
                                            </span>
                                        </div>
                                        {kycLabel && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-400">KYC</span>
                                                <span className="text-gray-600 dark:text-gray-300">{kycLabel}</span>
                                            </div>
                                        )}
                                        {summary && (
                                            <>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-400">Standing</span>
                                                    <span className={
                                                        summary.standing === 'poor' ? 'text-red-500 dark:text-red-400 capitalize' :
                                                        summary.standing === 'active' ? 'text-[#1e3a6e] dark:text-blue-300 capitalize' :
                                                        summary.standing === 'repeat' ? 'text-emerald-600 dark:text-emerald-400 capitalize' :
                                                        'text-gray-500 dark:text-gray-400 capitalize'
                                                    }>
                                                        {summary.standing === 'repeat' ? 'Repeat client' :
                                                         summary.standing === 'active' ? 'Active borrower' :
                                                         summary.standing === 'poor' ? 'Poor' :
                                                         'New client'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-400">Active</span>
                                                    <span className="text-gray-600 dark:text-gray-300">
                                                        {summary.activeCount === 0
                                                            ? 'None'
                                                            : `${summary.activeCount} · ${summary.activeProducts.join(', ')}`}
                                                    </span>
                                                </div>
                                                {summary.pendingCount > 0 && (
                                                    <div className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-400">Pending</span>
                                                        <span className="text-amber-600 dark:text-amber-400">{summary.pendingCount}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* History (from /client-summary) */}
                                    <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                                        <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-2">History</p>
                                        {!summary ? (
                                            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                                                <HugeiconsIcon icon={Clock01Icon} size={12} className="text-gray-400 shrink-0 animate-pulse" />
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Loading history…</p>
                                            </div>
                                        ) : summary.history.length === 0 ? (
                                            <div className="px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                                                <p className="text-xs text-gray-500 dark:text-gray-400">No prior applications</p>
                                            </div>
                                        ) : (
                                            <ul className="space-y-2">
                                                {summary.history.map((row, i) => (
                                                    <li key={i} className="flex items-start justify-between gap-2 text-xs">
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-gray-700 dark:text-gray-200 truncate capitalize">{row.product}</p>
                                                            <p className="text-gray-400 tabular-nums">
                                                                GHS {Number(row.amount).toLocaleString()}
                                                                {row.date && (
                                                                    <> · {new Date(row.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}</>
                                                                )}
                                                            </p>
                                                        </div>
                                                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] capitalize ${
                                                            row.state === 'active' ? 'bg-blue-50 text-[#1e3a6e] dark:bg-blue-500/10 dark:text-blue-300' :
                                                            row.state === 'completed' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' :
                                                            row.state === 'rejected' || row.state === 'terminated' ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300' :
                                                            'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                                        }`}>
                                                            {row.state}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="pt-2 pb-4 text-center">
                                    <div className="mx-auto w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
                                        <HugeiconsIcon icon={UserIcon} size={16} className="text-gray-400" />
                                    </div>
                                    <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                                        Look up a client<br />by phone number
                                    </p>
                                </div>
                            )}
                        </aside>

                        {/* ─────────── RIGHT AREA: PRODUCTS ─────────── */}
                        <div className="space-y-4">

                            {!clientReady ? (
                                /* Empty state */
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-10 text-center">
                                    <div className="mx-auto w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
                                        <HugeiconsIcon icon={SmartPhone01Icon} size={20} className="text-gray-400" />
                                    </div>
                                    <p className="mt-3 text-[15px] font-medium text-gray-700 dark:text-gray-200">Find a client to get started</p>
                                    <p className="mt-1 text-sm text-gray-400 max-w-xs mx-auto leading-relaxed">
                                        Enter the client's phone number in the panel on the left. Loan products will appear here.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Product picker */}
                                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                        <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-3">Pick a product</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                                            {loanProducts?.map((product) => {
                                                const slug = product.value ?? product.slug;
                                                const isSelected = loanType === slug;
                                                return (
                                                    <button
                                                        key={product.id}
                                                        type="button"
                                                        onClick={() => setLoanType(slug)}
                                                        className={`text-left rounded-xl border px-3 py-2.5 transition-all focus:outline-none ${
                                                            isSelected
                                                                ? 'border-[#1e3a6e] bg-[#1e3a6e]/[0.04] dark:bg-blue-500/10 ring-1 ring-[#1e3a6e]/10'
                                                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-[#1e3a6e]/40 hover:bg-blue-50/30 dark:hover:bg-blue-500/5'
                                                        }`}
                                                    >
                                                        <p className={`text-sm font-semibold leading-tight ${isSelected ? 'text-[#1e3a6e] dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>
                                                            {product.name}
                                                        </p>
                                                        <p className={`text-[11px] mt-1 tabular-nums ${isSelected ? 'text-[#1e3a6e]/70 dark:text-blue-400' : 'text-gray-400'}`}>
                                                            {product.limit?.toLocaleString()} · {product.rate}% p.m.
                                                        </p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Detail panel */}
                                    {selectedProduct ? (
                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 space-y-4">
                                            <div>
                                                <p className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">{selectedProduct.name}</p>
                                                {selectedProduct.summary && (
                                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{selectedProduct.summary}</p>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-3 gap-3 py-3 border-y border-gray-100 dark:border-gray-700">
                                                <div>
                                                    <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-1">Max</p>
                                                    <p className="text-[15px] font-semibold text-gray-800 dark:text-gray-100 tabular-nums">GHS {selectedProduct.limit?.toLocaleString()}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-1">Rate</p>
                                                    <p className="text-[15px] font-semibold text-gray-800 dark:text-gray-100 tabular-nums">{selectedProduct.rate}% p.m.</p>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-1">Applicant</p>
                                                    <p className="text-[15px] font-semibold text-gray-800 dark:text-gray-100 truncate">{client?.otherNames}</p>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={handleNav}
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] transition-colors"
                                            >
                                                Start {selectedProduct.name} application
                                                <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 px-5 py-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                                            <HugeiconsIcon icon={Clock01Icon} size={16} className="text-gray-400 shrink-0" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Select a product above to preview its terms and start the application.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>
                    </div>

                </section>
            </main>

            <NewClient
                isOpen={isOpen}
                closeModal={() => setIsOpen(false)}
                phone={phoneNum}
                setClient={setClient}
                setIsToggled={setIsToggled}
            />
        </div>
    );
}

export default Index;
